/**
 * Image URL Utility
 * Normalizes image paths for use in React components
 * Handles webpack require() paths and absolute/relative paths
 */

/**
 * Normalize image URL - return original path without Cloudflare transformation
 * @param {string} imagePath - Original image path (e.g., '/images/tour/bluelagoon.jpg' or './review/review (43).jpg')
 * @param {string} options - Ignored (kept for backward compatibility)
 * @returns {string} - Normalized image path
 */
export const transformImageUrl = (imagePath, options) => {
    if (!imagePath) return '';
    
    // Convert to string if needed (for webpack require() results)
    let pathToTransform = String(imagePath);
    
    // Handle webpack require() results - get the actual path
    if (typeof imagePath === 'object' && imagePath.default) {
        pathToTransform = String(imagePath.default);
    }
    
    // Handle relative paths starting with ./component/
    if (pathToTransform.startsWith('./component/')) {
        // Remove ./ and use absolute path from root
        return pathToTransform.substring(1); // becomes /component/...
    }
    
    // Handle other relative paths
    if (pathToTransform.startsWith('./')) {
        return pathToTransform.substring(1); // Remove ./
    }
    
    // For paths already starting with / or http(s)://, return as-is
    if (pathToTransform.startsWith('/') || pathToTransform.startsWith('http://') || pathToTransform.startsWith('https://')) {
        return pathToTransform;
    }
    
    // For webpack-processed paths (usually start with /static/media/), return as-is
    if (pathToTransform.startsWith('/static/')) {
        return pathToTransform;
    }
    
    // Ensure path starts with / if it's a local path
    if (!pathToTransform.startsWith('/') && !pathToTransform.startsWith('http')) {
        return '/' + pathToTransform;
    }
    
    return pathToTransform;
};

/**
 * Generate responsive srcset for images
 * @param {string} imagePath - Original image path
 * @param {object} config - Configuration object (ignored, kept for backward compatibility)
 * @returns {string} - Empty string (srcset not used without image transformation)
 */
export const generateSrcset = (imagePath, config = {}) => {
    // Don't use srcset if not using image transformation
    return '';
};

/**
 * Generate sizes attribute for responsive images
 * @param {object} config - Configuration object with breakpoints
 * @returns {string} - sizes string
 */
export const generateSizes = (config = {}) => {
    const { 
        mobile = '400px', 
        tablet = '800px', 
        desktop = '1200px',
        tabletBreakpoint = '640px',
        desktopBreakpoint = '1024px'
    } = config;
    
    return `(max-width: ${tabletBreakpoint}) ${mobile}, (max-width: ${desktopBreakpoint}) ${tablet}, ${desktop}`;
};

/**
 * Image transformation presets
 */
export const IMAGE_PRESETS = {
    // Hero/Banner images
    hero: {
        options: 'width=1920,quality=80,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 80, widths: [640, 1280, 1920] }),
        sizes: generateSizes({ mobile: '100vw', tablet: '100vw', desktop: '1920px' })
    },
    
    // Destination/Tour images (homepage)
    destination: {
        options: 'width=800,height=600,fit=cover,quality=85,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 85, widths: [400, 800, 1200] }),
        sizes: generateSizes({ mobile: '400px', tablet: '800px', desktop: '1200px' })
    },
    
    // Thumbnail images
    thumbnail: {
        options: 'width=400,height=300,fit=cover,quality=80,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 80, widths: [200, 400, 600] }),
        sizes: generateSizes({ mobile: '400px', tablet: '400px', desktop: '400px' })
    },
    
    // Detail page images
    detail: {
        options: 'width=1200,quality=85,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 85, widths: [600, 1200, 1920] }),
        sizes: generateSizes({ mobile: '100vw', tablet: '800px', desktop: '1200px' })
    },
    
    // Gallery images
    gallery: {
        options: 'width=600,height=400,fit=cover,quality=85,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 85, widths: [300, 600, 900] }),
        sizes: generateSizes({ mobile: '100vw', tablet: '50vw', desktop: '600px' })
    },
    
    // Review/Gallery images (square/carousel)
    review: {
        options: 'width=600,height=600,fit=cover,quality=85,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 85, widths: [300, 600, 900] }),
        sizes: generateSizes({ mobile: '50vw', tablet: '33vw', desktop: '300px' })
    },
    
    // Blog card images
    blogCard: {
        options: 'width=800,height=450,fit=cover,quality=85,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 85, widths: [400, 800, 1200] }),
        sizes: generateSizes({ mobile: '100vw', tablet: '50vw', desktop: '400px' })
    },
    
    // Blog featured images
    blogFeatured: {
        options: 'width=1200,height=675,fit=cover,quality=85,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 85, widths: [640, 1200, 1920] }),
        sizes: generateSizes({ mobile: '100vw', tablet: '100vw', desktop: '1200px' })
    },
    
    // Popular/Blog list thumbnail
    blogThumbnail: {
        options: 'width=300,height=300,fit=cover,quality=80,format=auto',
        srcset: (path) => generateSrcset(path, { quality: 80, widths: [150, 300, 450] }),
        sizes: generateSizes({ mobile: '300px', tablet: '300px', desktop: '300px' })
    }
};

/**
 * Transform image with preset
 * @param {string} imagePath - Original image path
 * @param {string} preset - Preset name (e.g., 'hero', 'destination', 'thumbnail')
 * @returns {object} - Object with src, srcset, and sizes
 */
export const transformImage = (imagePath, preset = 'thumbnail') => {
    const presetConfig = IMAGE_PRESETS[preset] || IMAGE_PRESETS.thumbnail;
    
    // Get the actual image path (handle webpack require() results)
    let actualPath = imagePath;
    if (typeof imagePath === 'object' && imagePath.default) {
        actualPath = imagePath.default;
    }
    
    return {
        src: transformImageUrl(imagePath, presetConfig.options),
        srcset: typeof presetConfig.srcset === 'function' 
            ? presetConfig.srcset(actualPath) 
            : presetConfig.srcset,
        sizes: presetConfig.sizes
    };
};

